import React, { forwardRef, useImperativeHandle, useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface AssociationPeriodFilterProps {
  api: any;
  column: any;
  columnApi: any;
  context: any;
}

const AssociationPeriodFilter = forwardRef((props: AssociationPeriodFilterProps, ref) => {
  const { t } = useTranslation();
  const [filterModel, setFilterModel] = useState<{ dateFrom: string; dateTo: string }>({ dateFrom: '', dateTo: '' });
  const currentFilterRef = useRef<{ dateFrom: string; dateTo: string }>({ dateFrom: '', dateTo: '' });

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return currentFilterRef.current.dateFrom !== '' || currentFilterRef.current.dateTo !== '';
    },
    doesFilterPass(params: any) {
      if (!this.isFilterActive()) return true;
      
      // Check if this is the DriverCellRenderer context (direct dateFrom/dateTo properties)
      if (params.data.dateFrom && params.data.dateTo) {
        const associationDateFrom = new Date(params.data.dateFrom);
        const associationDateTo = new Date(params.data.dateTo);
        
        const filterDateFrom = currentFilterRef.current.dateFrom ? new Date(currentFilterRef.current.dateFrom) : null;
        const filterDateTo = currentFilterRef.current.dateTo ? new Date(currentFilterRef.current.dateTo) : null;

        // Se non ci sono date di filtro, mostra tutto
        if (!filterDateFrom && !filterDateTo) return true;

        // Controlla se dateFrom è nel range del filtro
        if (filterDateFrom && filterDateTo) {
          // Range completo: controlla se dateFrom è nel range
          if (associationDateFrom >= filterDateFrom && associationDateFrom <= filterDateTo) {
            return true;
          }
          // Se dateFrom non è nel range, controlla se dateTo è nel range
          if (associationDateTo >= filterDateFrom && associationDateTo <= filterDateTo) {
            return true;
          }
          return false;
        } else if (filterDateFrom) {
          // Solo data di inizio: controlla se dateFrom è >= filterDateFrom
          if (associationDateFrom >= filterDateFrom) {
            return true;
          }
          // Se dateFrom non è >= filterDateFrom, controlla se dateTo è >= filterDateFrom
          if (associationDateTo >= filterDateFrom) {
            return true;
          }
          return false;
        } else if (filterDateTo) {
          // Solo data di fine: controlla se dateFrom è <= filterDateTo
          if (associationDateFrom <= filterDateTo) {
            return true;
          }
          // Se dateFrom non è <= filterDateTo, controlla se dateTo è <= filterDateTo
          if (associationDateTo <= filterDateTo) {
            return true;
          }
          return false;
        }

        return true;
      }
      
      // Original logic for FleetAndRoute context (with associations array)
      const today = new Date();
      const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];

      const todayIncluded = activeAssociations.find((a: any) => {
        const from = new Date(a.dateFrom);
        const to = new Date(a.dateTo);
        return from <= today && to >= today;
      });

      let selectedAssociation = todayIncluded;

      if (!selectedAssociation) {
        selectedAssociation = activeAssociations
          .filter((a: any) => new Date(a.dateFrom) > today)
          .sort((a: any, b: any) =>
            new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
          )[0];
      }

      if (!selectedAssociation) return false;

      const associationDateFrom = new Date(selectedAssociation.dateFrom);
      const associationDateTo = new Date(selectedAssociation.dateTo);
      
      const filterDateFrom = currentFilterRef.current.dateFrom ? new Date(currentFilterRef.current.dateFrom) : null;
      const filterDateTo = currentFilterRef.current.dateTo ? new Date(currentFilterRef.current.dateTo) : null;

      // Se non ci sono date di filtro, mostra tutto
      if (!filterDateFrom && !filterDateTo) return true;

      // Controlla se dateFrom è nel range del filtro
      if (filterDateFrom && filterDateTo) {
        // Range completo: controlla se dateFrom è nel range
        if (associationDateFrom >= filterDateFrom && associationDateFrom <= filterDateTo) {
          return true;
        }
        // Se dateFrom non è nel range, controlla se dateTo è nel range
        if (associationDateTo >= filterDateFrom && associationDateTo <= filterDateTo) {
          return true;
        }
        return false;
      } else if (filterDateFrom) {
        // Solo data di inizio: controlla se dateFrom è >= filterDateFrom
        if (associationDateFrom >= filterDateFrom) {
          return true;
        }
        // Se dateFrom non è >= filterDateFrom, controlla se dateTo è >= filterDateFrom
        if (associationDateTo >= filterDateFrom) {
          return true;
        }
        return false;
      } else if (filterDateTo) {
        // Solo data di fine: controlla se dateFrom è <= filterDateTo
        if (associationDateFrom <= filterDateTo) {
          return true;
        }
        // Se dateFrom non è <= filterDateTo, controlla se dateTo è <= filterDateTo
        if (associationDateTo <= filterDateTo) {
          return true;
        }
        return false;
      }

      return true;
    },
    getModel() {
      if (this.isFilterActive()) {
        return {
          dateFrom: currentFilterRef.current.dateFrom,
          dateTo: currentFilterRef.current.dateTo,
        };
      }
      return null;
    },
    setModel(model: any) {
      const newModel = {
        dateFrom: model?.dateFrom || '',
        dateTo: model?.dateTo || '',
      };
      setFilterModel(newModel);
      currentFilterRef.current = newModel;
    },
    getModelAsString() {
      if (!this.isFilterActive()) return '';
      const parts = [];
      if (currentFilterRef.current.dateFrom) {
        parts.push(`From: ${currentFilterRef.current.dateFrom}`);
      }
      if (currentFilterRef.current.dateTo) {
        parts.push(`To: ${currentFilterRef.current.dateTo}`);
      }
      return parts.join(', ');
    }
  }), []);

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newModel = { ...filterModel, [field]: value };
    setFilterModel(newModel);
    currentFilterRef.current = newModel;
    props.api.onFilterChanged();
  };

  const handleReset = () => {
    const newModel = { dateFrom: '', dateTo: '' };
    setFilterModel(newModel);
    currentFilterRef.current = newModel;
    props.api.onFilterChanged();
  };

  return (
    <Box sx={{ 
      width: '200px',
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1,
      p: 1.5
    }}>
      <Box sx={{ mb: 1.5 }}>
        <label style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#666',
          marginBottom: '4px'
        }}>
          Data di inizio
        </label>
        <input
          type="date"
          value={filterModel.dateFrom}
          onChange={(e) => handleDateChange('dateFrom', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            backgroundColor: '#fff',
            height: '24px',
            boxSizing: 'border-box'
          }}
          placeholder="gg/mm/aaaa"
        />
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <label style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#666',
          marginBottom: '4px'
        }}>
          Data di fine
        </label>
        <input
          type="date"
          value={filterModel.dateTo}
          onChange={(e) => handleDateChange('dateTo', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            backgroundColor: '#fff',
            height: '24px',
            boxSizing: 'border-box'
          }}
          placeholder="gg/mm/aaaa"
        />
      </Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        pt: 0.5
      }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleReset}
          sx={{ 
            minWidth: '60px',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.25,
            px: 1,
            borderColor: 'divider',
            color: '#CF0A2C',
            '&:hover': {
              borderColor: '#CF0A2C',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {t('common.reset')}
        </Button>
      </Box>
    </Box>
  );
});

export default AssociationPeriodFilter;
